import { TONE } from './constants'

export const CONDITIONAL_ACCESS_BASELINE_TEMPLATE_TAGS = {
  MFA_ALL_USERS: 'CIS_M365_V6_L1_MFA_FOR_ALL_USERS_5_2_2_2',
  BLOCK_LEGACY_AUTHENTICATION: 'CIS_M365_V6_L1_BLOCK_LEGACY_AUTHENTICATION_5_2_2_3',
  PHISHING_RESISTANT_MFA_ADMINS: 'CIS_M365_V6_L2_PHISHING_RESISTANT_MFA_FOR_ADMINISTRATORS_5_2_2_5',
  USER_RISK: 'CIS_M365_V6_L1_IDENTITY_PROTECTION_USER_RISK_POLICY_5_2_2_6',
  SIGN_IN_RISK: 'CIS_M365_V6_L1_IDENTITY_PROTECTION_SIGN_IN_RISK_POLICY_5_2_2_7',
  COMPLIANT_DEVICE: 'CIS_M365_V6_L1_MANAGED_DEVICE_REQUIRED_FOR_AUTHENTICATION_5_2_2_9',
}

const CA_REPORT_ONLY_STATES = new Set(['enabledforreportingbutnotenforced', 'reportonly', 'report_only'])
const PHISHING_RESISTANT_AUTH_STRENGTH_ID = '00000000-0000-0000-0000-000000000004'
const M365_APP_IDS = new Set([
  'office365',
  '00000003-0000-0ff1-ce00-000000000000',
  '00000002-0000-0ff1-ce00-000000000000',
  '00000003-0000-0000-c000-000000000000',
])

const lowerString = (value) => String(value ?? '').toLowerCase()
const arrayOf = (value) => (Array.isArray(value) ? value : [])
const getConditions = (policy) => policy.conditions ?? policy.rawPolicy?.conditions ?? {}
const getGrantControls = (policy) => policy.grantControls ?? policy.rawPolicy?.grantControls ?? {}
const getIncludeUsers = (policy) => arrayOf(getConditions(policy).users?.includeUsers).map(lowerString)
const getIncludeRoles = (policy) => arrayOf(getConditions(policy).users?.includeRoles)
const getBuiltInControls = (policy) => arrayOf(getGrantControls(policy).builtInControls).map(lowerString)
const getClientAppTypes = (policy) => arrayOf(getConditions(policy).clientAppTypes).map(lowerString)

export const getPolicyState = (policy) =>
  lowerString(policy.state || policy.policyStatus || policy.rawPolicy?.state || policy.stateRaw)

export const getPolicyName = (policy) =>
  policy.policyName || policy.rawPolicy?.displayName || policy.displayName || policy.policyId

export const isReportOnlyPolicy = (policy) => CA_REPORT_ONLY_STATES.has(getPolicyState(policy))
export const isEnforcedPolicy = (policy) => getPolicyState(policy) === 'enabled'

const targetsAllUsers = (policy) => getIncludeUsers(policy).includes('all')
const targetsAdminRoles = (policy) => getIncludeRoles(policy).length > 0
const grantsMfa = (policy) => getBuiltInControls(policy).includes('mfa')
const grantsBlock = (policy) => getBuiltInControls(policy).includes('block')
const grantsMfaOrBlock = (policy) => grantsMfa(policy) || grantsBlock(policy)

const requiresPhishingResistant = (policy) => {
  const strength = getGrantControls(policy).authenticationStrength
  if (!strength) return false
  if (lowerString(strength.id) === PHISHING_RESISTANT_AUTH_STRENGTH_ID) return true
  return /phishing/i.test(strength.displayName ?? '')
}

const requiresCompliantDevice = (policy) => {
  const controls = getBuiltInControls(policy)
  return controls.includes('compliantdevice') || controls.includes('domainjoineddevice')
}

const blocksLegacyAuthentication = (policy) => {
  const apps = getClientAppTypes(policy)
  return apps.includes('exchangeactivesync') && apps.includes('other') && grantsBlock(policy)
}

const hasSignInRiskCondition = (policy) => arrayOf(getConditions(policy).signInRiskLevels).length > 0
const hasUserRiskCondition = (policy) => arrayOf(getConditions(policy).userRiskLevels).length > 0
const grantsMfaOrPasswordChange = (policy) => {
  const controls = getBuiltInControls(policy)
  return controls.includes('mfa') || controls.includes('passwordchange')
}

const includesAllApps = (policy) =>
  arrayOf(getConditions(policy).applications?.includeApplications).map(lowerString).includes('all')

const includesM365Apps = (policy) => {
  const apps = arrayOf(getConditions(policy).applications?.includeApplications).map(lowerString)
  if (apps.includes('all')) return true
  return apps.some((id) => M365_APP_IDS.has(id))
}

const includesAllClientApps = (policy) => {
  const apps = getClientAppTypes(policy)
  return apps.length === 0 || apps.includes('all')
}

const includesLegacyClientApps = (policy) => {
  const apps = getClientAppTypes(policy)
  return apps.includes('exchangeactivesync') && apps.includes('other')
}

const REQ_ENABLED = { key: 'enabled', labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.ENABLED', check: isEnforcedPolicy }
const REQ_ALL_USERS = { key: 'allUsers', labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.ALL_USERS', check: targetsAllUsers }
const REQ_ADMIN_ROLES = {
  key: 'adminRoles',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.TARGETS_ADMIN_ROLES',
  check: targetsAdminRoles,
}
const REQ_ALL_APPS = { key: 'allApps', labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.ALL_APPS', check: includesAllApps }
const REQ_M365_APPS = { key: 'm365Apps', labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.M365_APPS', check: includesM365Apps }
const REQ_ALL_CLIENT_APPS = {
  key: 'allClientApps',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.ALL_CLIENT_APPS',
  check: includesAllClientApps,
}
const REQ_LEGACY_CLIENT_APPS = {
  key: 'legacyClientApps',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.LEGACY_CLIENT_APPS',
  check: includesLegacyClientApps,
}
const REQ_GRANT_MFA = { key: 'grantMfa', labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.GRANT_MFA', check: grantsMfa }
const REQ_GRANT_BLOCK = { key: 'grantBlock', labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.GRANT_BLOCK', check: grantsBlock }
const REQ_GRANT_COMPLIANT_DEVICE = {
  key: 'grantCompliantDevice',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.GRANT_COMPLIANT_DEVICE',
  check: requiresCompliantDevice,
}
const REQ_GRANT_PHISHING = {
  key: 'grantPhishing',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.GRANT_PHISHING',
  check: requiresPhishingResistant,
}
const REQ_GRANT_MFA_OR_BLOCK = {
  key: 'grantMfaOrBlock',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.GRANT_MFA_OR_BLOCK',
  check: grantsMfaOrBlock,
}
const REQ_GRANT_RECOVERY = {
  key: 'grantRecovery',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.GRANT_RECOVERY',
  check: grantsMfaOrPasswordChange,
}
const REQ_SIGNIN_RISK = {
  key: 'signInRisk',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.SIGNIN_RISK_SET',
  check: hasSignInRiskCondition,
}
const REQ_USER_RISK = {
  key: 'userRisk',
  labelId: 'AI_RISK_REPORT.IDENTITY.CA.REQ.USER_RISK_SET',
  check: hasUserRiskCondition,
}

// Targetted list of baselines we care to show in the AI risk report
export const CONDITIONAL_ACCESS_BASELINE_PATTERNS = [
  {
    key: 'mfa-all-users',
    templateTag: CONDITIONAL_ACCESS_BASELINE_TEMPLATE_TAGS.MFA_ALL_USERS,
    labelId: 'AI_RISK_REPORT.IDENTITY.CA.BASELINE.PATTERN.MFA_ALL',
    matches: grantsMfa,
    requirements: [REQ_ENABLED, REQ_ALL_USERS, REQ_ALL_APPS, REQ_ALL_CLIENT_APPS, REQ_GRANT_MFA],
  },
  {
    key: 'block-legacy',
    templateTag: CONDITIONAL_ACCESS_BASELINE_TEMPLATE_TAGS.BLOCK_LEGACY_AUTHENTICATION,
    labelId: 'AI_RISK_REPORT.IDENTITY.CA.BASELINE.PATTERN.BLOCK_LEGACY',
    matches: blocksLegacyAuthentication,
    requirements: [REQ_ENABLED, REQ_ALL_USERS, REQ_LEGACY_CLIENT_APPS, REQ_GRANT_BLOCK],
  },
  {
    key: 'phish-admins',
    templateTag: CONDITIONAL_ACCESS_BASELINE_TEMPLATE_TAGS.PHISHING_RESISTANT_MFA_ADMINS,
    labelId: 'AI_RISK_REPORT.IDENTITY.CA.BASELINE.PATTERN.PHISH_ADMINS',
    matches: requiresPhishingResistant,
    requirements: [REQ_ENABLED, REQ_ADMIN_ROLES, REQ_ALL_APPS, REQ_GRANT_PHISHING],
  },
  {
    key: 'signin-risk',
    templateTag: CONDITIONAL_ACCESS_BASELINE_TEMPLATE_TAGS.SIGN_IN_RISK,
    labelId: 'AI_RISK_REPORT.IDENTITY.CA.BASELINE.PATTERN.SIGNIN_RISK',
    matches: hasSignInRiskCondition,
    requirements: [REQ_ENABLED, REQ_ALL_USERS, REQ_ALL_APPS, REQ_SIGNIN_RISK, REQ_GRANT_MFA_OR_BLOCK],
  },
  {
    key: 'user-risk',
    templateTag: CONDITIONAL_ACCESS_BASELINE_TEMPLATE_TAGS.USER_RISK,
    labelId: 'AI_RISK_REPORT.IDENTITY.CA.BASELINE.PATTERN.USER_RISK',
    matches: hasUserRiskCondition,
    requirements: [REQ_ENABLED, REQ_ALL_USERS, REQ_ALL_APPS, REQ_USER_RISK, REQ_GRANT_RECOVERY],
  },
  {
    key: 'compliant-device',
    templateTag: CONDITIONAL_ACCESS_BASELINE_TEMPLATE_TAGS.COMPLIANT_DEVICE,
    labelId: 'AI_RISK_REPORT.IDENTITY.CA.BASELINE.PATTERN.COMPLIANT_DEVICE',
    matches: requiresCompliantDevice,
    requirements: [REQ_ENABLED, REQ_ALL_USERS, REQ_M365_APPS, REQ_ALL_CLIENT_APPS, REQ_GRANT_COMPLIANT_DEVICE],
  },
]

const policyStateRank = (policy) => {
  if (isEnforcedPolicy(policy)) return 2
  if (isReportOnlyPolicy(policy)) return 1
  return 0
}

export const evaluateBaselinePattern = (pattern, policies = []) => {
  const total = pattern.requirements.length
  const candidates = policies.filter(pattern.matches)

  if (candidates.length === 0) {
    return { bestPolicy: null, requirements: [], passed: 0, total, tone: TONE.NEUTRAL }
  }

  const [best] = candidates
    .map((policy) => {
      const requirements = pattern.requirements.map((req) => ({
        key: req.key,
        labelId: req.labelId,
        met: req.check(policy),
      }))

      return { policy, requirements, passed: requirements.filter((req) => req.met).length }
    })
    .sort((first, second) => {
      if (second.passed !== first.passed) return second.passed - first.passed
      return policyStateRank(second.policy) - policyStateRank(first.policy)
    })

  return {
    bestPolicy: best.policy,
    requirements: best.requirements,
    passed: best.passed,
    total,
    tone: best.passed === total && isEnforcedPolicy(best.policy) ? TONE.OK : TONE.WARN,
  }
}

export const buildConditionalAccessBaselineRows = (policies = []) =>
  CONDITIONAL_ACCESS_BASELINE_PATTERNS.map((pattern) => ({
    key: pattern.key,
    pattern,
    ...evaluateBaselinePattern(pattern, policies),
  }))

export const buildConditionalAccessPolicyStats = ({ policies = [], summary = {} } = {}) => {
  if (policies.length === 0) {
    return {
      total: summary.total ?? 0,
      enabled: summary.enabled ?? 0,
      reportOnly: summary.reportOnly ?? 0,
      enabledPct: summary.enabledPct ?? 0,
    }
  }

  const total = policies.length
  const enabled = policies.filter(isEnforcedPolicy).length
  const reportOnly = policies.filter(isReportOnlyPolicy).length
  const enabledPct = total > 0 ? Math.round((enabled / total) * 100) : 0

  return { total, enabled, reportOnly, enabledPct }
}
